import { useCallback, useEffect, useState } from 'react'
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import SmallCard from '../cards/small-card'
import discoverApi from '../../client/discover-api'
import { DiscoverItem, Mood } from '../../types/Discover'

// "What are you in the mood for?" - a chip row of server-defined moods.
// Selecting one swaps in a rail of matching titles; selecting it again (or
// hitting refresh) re-rolls, since the backend randomizes the results page.

const StyledSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
}))

const StyledChipRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  paddingTop: theme.spacing(1),
  overflowX: 'auto',
  paddingBottom: theme.spacing(1),
  // Match the card scrollers: usable on touch, no scrollbar chrome elsewhere.
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
}))

const StyledMoodChip = styled('button', {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected: boolean }>(({ theme, selected }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  padding: theme.spacing(1, 2),
  borderRadius: 999,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontFamily: theme.typography.fontFamily,
  fontWeight: selected ? 600 : 400,
  color: selected ? theme.palette.common.white : theme.palette.text.primary,
  background: selected
    ? 'linear-gradient(135deg, #8B1E3F 0%, #4A0E1F 100%)'
    : theme.palette.background.paper,
  border: `1px solid ${selected ? 'transparent' : theme.palette.divider}`,
  transition: 'all 0.2s ease',
  flexShrink: 0,
  '@media (hover: hover) and (pointer: fine)': {
    '&:hover': {
      borderColor: '#8B1E3F',
      transform: 'translateY(-1px)',
    },
  },
}))

const StyledResultsScroller = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  overflowX: 'auto',
  paddingBottom: theme.spacing(1),
  // Theme the scrollbar so it isn't the bright OS-default bar in dark mode.
  '&::-webkit-scrollbar': {
    height: 6,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.divider,
    borderRadius: 3,
  },
}))

const StyledLoadingRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 200,
}))

const StyledHeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
}))

const MoodPicker = () => {
  const [moods, setMoods] = useState<Mood[]>([])
  const [selected, setSelected] = useState<Mood | null>(null)
  const [results, setResults] = useState<DiscoverItem[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    discoverApi
      .getMoods()
      .then(setMoods)
      .catch(() => setMoods([]))
  }, [])

  const fetchResults = useCallback(async (mood: Mood) => {
    setLoading(true)
    try {
      const items = await discoverApi.getMoodResults(mood.key, 20)
      setResults(items)
    } catch {
      setResults([])
    } finally {
      setLoading(false)
    }
  }, [])

  const handleSelect = (mood: Mood) => {
    if (selected?.key === mood.key) {
      // Tapping the active mood again deselects and collapses the rail.
      setSelected(null)
      setResults([])
      return
    }
    setSelected(mood)
    fetchResults(mood)
  }

  // The catalog failing to load just hides the section; the home page must
  // never break because of an optional rail.
  if (moods.length === 0) return null

  return (
    <StyledSection>
      <StyledHeaderRow>
        <Typography variant='h5' fontWeight={600}>
          What are you in the mood for?
        </Typography>
        {selected && (
          <Tooltip title='Show me different ones'>
            <IconButton
              size='small'
              onClick={() => fetchResults(selected)}
              disabled={loading}
              aria-label='Refresh mood results'
            >
              <RefreshRoundedIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        )}
      </StyledHeaderRow>

      <StyledChipRow>
        {moods.map((mood) => (
          <StyledMoodChip
            key={mood.key}
            selected={selected?.key === mood.key}
            onClick={() => handleSelect(mood)}
            title={mood.description}
          >
            <span aria-hidden>{mood.emoji}</span>
            {mood.label}
          </StyledMoodChip>
        ))}
      </StyledChipRow>

      {selected && loading && (
        <StyledLoadingRow>
          <CircularProgress size={32} />
        </StyledLoadingRow>
      )}

      {selected && !loading && results.length > 0 && (
        <StyledResultsScroller>
          {results.map((item) => (
            <SmallCard
              key={`${item.type}-${item.id}`}
              id={item.id}
              type={item.type}
              title={item.title}
              year={item.year || ''}
              rating={item.rating ?? 0}
              posterUrl={item.posterUrl}
              genres={item.genres}
            />
          ))}
        </StyledResultsScroller>
      )}

      {selected && !loading && results.length === 0 && (
        <Typography color='text.secondary' sx={{ py: 4, textAlign: 'center' }}>
          Nothing matched this mood right now - try another one.
        </Typography>
      )}
    </StyledSection>
  )
}

export default MoodPicker
